import { ArrowLeft, RefreshCcw } from 'lucide-react'
import { useState } from 'react'

import { currencies } from '@ankor-io/common/util/currencies'
import { formatCurrency, formatThousandsOnly } from '@ankor-io/common/util/currencies'

import { Button } from '@/ui-components/Button'
import { Field, Label } from '@/ui-components/Fieldset'
import { Heading } from '@/ui-components/Heading'
import { Input } from '@/ui-components/Input'
import { Select } from '@/ui-components/Select'

const MOCK_CONVERSION = {
  fromCurrency: 'EUR',
  toCurrency: 'AUD',
  rate: 1.787605,
  updatedAt: '2025-09-09T09:36:19Z',
}

const MOCK_AVAILABLE_BALANCE_FROM = {
  currency: 'EUR',
  amount: 7876.9,
}

const MOCK_AVAILABLE_BALANCE_TO = {
  currency: 'AUD',
  amount: 0.0,
}

const NewConversionPage: React.FC = () => {
  const [fromAmount, setFromAmount] = useState('1000')
  const [fromCurrency, setFromCurrency] = useState(MOCK_AVAILABLE_BALANCE_FROM.currency)

  const [toAmount, setToAmount] = useState('400')
  const [toCurrency, setToCurrency] = useState(MOCK_AVAILABLE_BALANCE_TO.currency)

  const handleAmountChange = (value: string) => {
    // Remove any non-numeric characters except commas
    const cleaned = value.replace(/[^0-9,]/g, '')
    const numericValue = cleaned.replace(/,/g, '')
    let newFromAmount: string
    if (numericValue === '') {
      newFromAmount = ''
      setFromAmount(newFromAmount)
    } else {
      const numValue = parseFloat(numericValue)
      if (isNaN(numValue)) {
        return
      }

      newFromAmount = numValue.toString()
      setFromAmount(newFromAmount)
    }

    const computed = Number(newFromAmount) * MOCK_CONVERSION.rate
    if (isNaN(computed)) {
      setToAmount('')
    } else {
      setToAmount(computed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
    }
  }

  const handleAmountBlur = (value: string) => {
    // Format with thousands separators on blur
    const formatted = formatThousandsOnly(value)
    if (!formatted) {
      return
    }

    const numericValue = formatted.replace(/,/g, '')
    const numValue = parseFloat(numericValue)
    if (!isNaN(numValue)) {
      const newFromAmount = numValue.toString()
      setFromAmount(newFromAmount)
      const computed = Number(newFromAmount) * MOCK_CONVERSION.rate
      if (isNaN(computed)) {
        setToAmount('')
      } else {
        setToAmount(computed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
      }
    }
  }

  return (
    <div className="flex flex-col justify-between h-[calc(100vh-7.5rem)]">
      <div className="space-y-6">
        <Button
          plain
          to="/wallet"
        >
          <ArrowLeft data-slot="icon" />
          Back
        </Button>

        <Heading level={3}>New Conversion</Heading>

        <div className="flex flex-col gap-y-4">
          {/* From row */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Field className="sm:basis-3/4 sm:shrink sm:min-w-0">
              <Label>From</Label>
              <Input
                type="text"
                placeholder="$1,000"
                value={fromAmount ? formatThousandsOnly(fromAmount.toString()) : ''}
                onChange={(e) => handleAmountChange(e.target.value)}
                onBlur={(e) => handleAmountBlur(e.target.value)}
              />
              <div className="flex gap-x-2 mt-1">
                <Button
                  outline
                  onClick={() => console.log('Refresh clicked!')}
                >
                  <RefreshCcw data-slot="icon" />
                </Button>
                <div className="flex flex-col text-sm">
                  <span>
                    Current Rate:{' '}
                    <span className="font-bold">
                      1 {MOCK_CONVERSION.fromCurrency} = {MOCK_CONVERSION.rate} {MOCK_CONVERSION.toCurrency}
                    </span>
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Refreshed at {new Date(MOCK_CONVERSION.updatedAt).toLocaleString('en-US')}
                  </span>
                </div>
              </div>
            </Field>
            <Field className="sm:basis-1/4 sm:shrink-0">
              <Label>Currency</Label>
              <Select
                value={fromCurrency}
                onChange={(e) => setFromCurrency(e.target.value)}
              >
                <option value="">Select currency</option>
                {currencies.map((currency) => (
                  <option
                    key={currency.code}
                    value={currency.code}
                  >
                    {currency.code}
                  </option>
                ))}
              </Select>
              <div className="mt-1 text-sm text-right">
                Available balance:{' '}
                {formatCurrency(MOCK_AVAILABLE_BALANCE_FROM.amount, MOCK_AVAILABLE_BALANCE_FROM.currency)}
              </div>
            </Field>
          </div>

          {/* To row */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Field className="sm:basis-3/4 sm:shrink sm:min-w-0">
              <Label>To</Label>
              <Input
                type="text"
                inputMode="decimal"
                placeholder="$1,000"
                value={toAmount}
                readOnly
              />
            </Field>
            <Field className="sm:basis-1/4 sm:shrink-0">
              <Label>Currency</Label>
              <Select
                value={toCurrency}
                onChange={(e) => setToCurrency(e.target.value)}
              >
                <option value="">Select currency</option>
                {currencies.map((currency) => (
                  <option
                    key={currency.code}
                    value={currency.code}
                  >
                    {currency.code}
                  </option>
                ))}
              </Select>
              <div className="mt-1 text-sm text-right">
                Available balance:{' '}
                {formatCurrency(MOCK_AVAILABLE_BALANCE_TO.amount, MOCK_AVAILABLE_BALANCE_TO.currency)}
              </div>
            </Field>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-x-2">
        <Button
          outline
          onClick={() => console.log('Cancel clicked!')}
        >
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={() => console.log('Create Conversion clicked!')}
        >
          Create Conversion
        </Button>
      </div>
    </div>
  )
}

export default NewConversionPage
